/**
 * NU GUI gateway origin for the Lane A token broker (#590, decision D11).
 *
 * Same mechanism as the Lane A RPC URL (#940, src/services/rpc/rpcUrl.ts),
 * not a fork of it: the production container writes `gatewayUrl` into the
 * same `/runtime-config.json` (infra/frontend/write-runtime-config.cjs, from
 * the runtime env `NUDATA_GATEWAY_URL`), so one image serves prod and staging
 * — and each gateway brokers for exactly one console origin, so staging MUST
 * point at the staging gateway. Resolution order:
 *
 *   1. runtime `gatewayUrl` (production bundles only);
 *   2. build-time `VITE_GATEWAY_URL`;
 *   3. none — the broker is not configured, Lane A calls stay
 *      unauthenticated and Lane-A-owned screens render their honest
 *      "unavailable" state. There is NO default origin: guessing one would
 *      send the console token to a host nobody configured.
 *
 * FAIL CLOSED like resolveRpcUrl (#940/#952): a TRANSIENT failure to read the
 * runtime config (network error/timeout, 5xx, 408, 429, a body dropped
 * mid-read) yields null — broker unavailable for this attempt, not cached,
 * retried on the next call. It never falls back to the build-time origin,
 * which in a released image is PROD's gateway: a staging console would send
 * its token there. Only definitive "no runtime value" answers fall back.
 */

/* global AbortController -- browser global missing from the src/ eslint env */

import { RUNTIME_CONFIG_PATH } from "../rpc/rpcUrl";

const FETCH_TIMEOUT_MS = 3000;

/** An absolute http(s) ORIGIN with no credentials; normalised to `origin`. */
export function toGatewayOrigin(value: unknown): string | null {
  if (typeof value !== "string" || value.length === 0 || value.length > 2048) {
    return null;
  }
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return null;
  }
  if (url.protocol !== "https:" && url.protocol !== "http:") return null;
  if (url.username || url.password || url.search || url.hash) return null;
  if (url.pathname !== "/") return null;
  return url.origin;
}

export function buildTimeGatewayUrl(): string | null {
  return toGatewayOrigin(
    (import.meta.env as Record<string, unknown>).VITE_GATEWAY_URL,
  );
}

function runtimeConfigEnabled(): boolean {
  const prod = (import.meta.env as Record<string, unknown>).PROD;
  return prod === true || prod === "true";
}

let cached: { value: string | null } | null = null;
let inflight: Promise<string | null> | null = null;

async function loadRuntime(): Promise<{
  value: string | null;
  final: boolean;
}> {
  const fallback = buildTimeGatewayUrl();
  const controller =
    typeof AbortController !== "undefined" ? new AbortController() : null;
  const timer = controller
    ? setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS)
    : null;
  try {
    const res = await fetch(RUNTIME_CONFIG_PATH, {
      cache: "no-store",
      credentials: "same-origin",
      headers: { Accept: "application/json" },
      signal: controller?.signal,
    });
    if (res.status >= 500 || res.status === 408 || res.status === 429) {
      return { value: null, final: false };
    }
    if (!res.ok) return { value: fallback, final: true };
    let body: unknown;
    try {
      body = await res.json();
    } catch (err) {
      if (controller?.signal.aborted || !(err instanceof SyntaxError)) {
        return { value: null, final: false }; // dropped mid-body: transient
      }
      return { value: fallback, final: true }; // HTML/non-JSON: no config
    }
    const raw =
      body && typeof body === "object"
        ? (body as { gatewayUrl?: unknown }).gatewayUrl
        : undefined;
    if (raw === undefined || raw === null)
      return { value: fallback, final: true };
    const origin = toGatewayOrigin(raw);
    if (!origin) {
      console.warn(
        `[gatewayUrl] ignoring invalid runtime gatewayUrl in ${RUNTIME_CONFIG_PATH}`,
      );
      return { value: fallback, final: true };
    }
    return { value: origin, final: true };
  } catch {
    return { value: null, final: false }; // network error / timeout
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/** The gateway origin, or null when the broker is not configured. */
export function resolveGatewayUrl(): Promise<string | null> {
  if (cached) return Promise.resolve(cached.value);
  if (!runtimeConfigEnabled()) {
    cached = { value: buildTimeGatewayUrl() };
    return Promise.resolve(cached.value);
  }
  if (!inflight) {
    inflight = loadRuntime().then(({ value, final }) => {
      if (final) cached = { value };
      inflight = null;
      return value;
    });
  }
  return inflight;
}

/** Test seam. */
export function resetGatewayUrlForTests(): void {
  cached = null;
  inflight = null;
}
